import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { UserContextService } from '../../core/auth/user-context.service';
import { InMemoryDataService } from '../../core/data/in-memory-data.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';

@Component({
  selector: 'app-user-management-page',
  standalone: true,
  imports: [
    RouterLink,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    TranslatePipe
  ],
  template: `
    <div class="page-shell">
      @if (!canAccessUserManagement()) {
      <mat-card>
        <mat-card-content>
          <p>{{ 'context.userManagement.comingSoon' | t }}</p>
        </mat-card-content>
      </mat-card>
      } @else {
      <div class="page-header">
        <h1>{{ 'context.userManagement.title' | t }}</h1>
        <a mat-stroked-button [routerLink]="['/context']">
          <mat-icon>arrow_back</mat-icon>
          {{ 'resourceDetails.backToList' | t }}
        </a>
      </div>

      <mat-card>
        <mat-card-content>
          <p>{{ 'context.userManagement.description' | t }}</p>
          <p class="muted">{{ 'context.userManagement.comingSoon' | t }}</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-content>
          <h3>{{ 'context.userManagement.addUser' | t }}</h3>
          <div class="form-grid">
            <mat-form-field>
              <mat-label>{{ 'context.userManagement.organisation' | t }}</mat-label>
              <mat-select [value]="selectedOrganisationId()" (valueChange)="selectedOrganisationId.set($event)">
                @for (item of organisations(); track item.id) {
                  <mat-option [value]="item.id">{{ item.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'context.userManagement.name' | t }}</mat-label>
              <input matInput [value]="newUserName()" (input)="newUserName.set(($any($event.target).value ?? '').trimStart())" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'context.userManagement.role' | t }}</mat-label>
              <mat-select [value]="newUserRole()" (valueChange)="newUserRole.set($event)">
                <mat-option value="office">office</mat-option>
                <mat-option value="admin">admin</mat-option>
              </mat-select>
            </mat-form-field>
            <button mat-flat-button color="primary" [disabled]="!canAddUser()" (click)="addUser()">
              {{ 'context.userManagement.addUser' | t }}
            </button>
          </div>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-content>
          <h3>{{ 'toolbar.currentUser' | t }}</h3>
          <ul>
            @for (item of organisationUsers(); track item.id) {
              <li>{{ item.displayName }} ({{ item.role }})</li>
            } @empty {
              <li>{{ 'context.userManagement.noUsers' | t }}</li>
            }
          </ul>
        </mat-card-content>
      </mat-card>
      }
    </div>
  `,
  styles: `
    .page-shell { display: grid; gap: 14px; max-width: 960px; }
    .page-header { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
    h1 { margin: 0; }
    .muted { opacity: 0.7; }
    .form-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; align-items: end; }
    @media (max-width: 960px) { .form-grid { grid-template-columns: 1fr; } }
  `
})
export class UserManagementPageComponent {
  private readonly userContext = inject(UserContextService);
  private readonly dataService = inject(InMemoryDataService);

  readonly organisations = this.dataService.organisations;
  readonly canAccessUserManagement = this.userContext.canAccessUserManagement;
  readonly selectedOrganisationId = signal<number>(this.userContext.currentOrganisationId());
  readonly newUserName = signal('');
  readonly newUserRole = signal<'office' | 'admin'>('office');

  readonly organisationUsers = computed(() =>
    this.userContext
      .users()
      .filter(
        (item) =>
          (item.role === 'office' || item.role === 'admin') &&
          (item.linkedOrganisationId ?? this.selectedOrganisationId()) === this.selectedOrganisationId()
      )
  );
  readonly canAddUser = computed(() => this.newUserName().trim().length > 0);

  addUser(): void {
    this.userContext.addOrganisationUser({
      displayName: this.newUserName(),
      role: this.newUserRole(),
      organisationId: this.selectedOrganisationId()
    });
    this.newUserName.set('');
    this.newUserRole.set('office');
  }
}
